import { FC, useEffect } from 'react';
import {
  ActionsMenu,
  Button,
  Footer,
  ListTotal,
  Loader,
  SortableList,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Delete, Edit } from '@mui/icons-material';
import { ROUTES } from 'config';
import { usePointsLabel } from 'store/recognitions';
import { SystemColors } from 'melp-design/style';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form-latest';
import { useConfirm } from 'store/confirm';
import { usePredefinedToasts } from 'utils/Toast';
import {
  useRecognitionAchievements,
  useRemoveRecognitionAchievement,
  useUpdateAchievementsPriorities,
} from 'store/recognition-achievements';
import { useMe } from 'state/Administrators';
import { AchievementImage } from './atoms';

interface Values {
  achievements: Array<{ achievementId: string }>;
}

export const RecognitionsAchievementsLists: FC = () => {
  const history = useHistory();
  const predefinedToasts = usePredefinedToasts();
  const { hasEditPermission } = useMe();
  const { t } = useTranslation();
  const { confirmation } = useConfirm();

  const pointsLabel = usePointsLabel();

  const { data: achievements, isLoading } = useRecognitionAchievements();

  const { mutate: removeAchievement, isLoading: isRemoving } =
    useRemoveRecognitionAchievement();
  const { mutate: updatePriorities, isLoading: isUpdatingPriorities } =
    useUpdateAchievementsPriorities();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: {
      achievements: [],
    },
  });
  const { fields: achiementsFields, move: moveAchievements } = useFieldArray({
    name: 'achievements',
    control,
  });

  useEffect(() => {
    if (achievements) {
      reset({
        achievements: achievements.map((achievement) => ({
          achievementId: achievement.id,
        })),
      });
    }
  }, [achievements, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updatePriorities({
      priorities: values.achievements.map((achievement, i) => ({
        id: achievement.achievementId,
        index: i,
      })),
    });
  };

  if (isLoading) return <Loader />;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <TableWrapper>
        <TableToolbar>
          <Stack gap={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
            >
              <ListTotal total={achievements?.length ?? 0} />

              <ActionsMenu
                type="spread"
                items={[
                  {
                    label: t('recognition.createAchievementTitle'),
                    color: 'primary',
                    disabled: !hasEditPermission(['employeeRecognition']),
                    onClick: () =>
                      history.push(ROUTES.recognitions.achievements.create),
                  },
                ]}
              />
            </Stack>

            <SortableList
              key={JSON.stringify(
                achiementsFields.map(
                  (achievement) => achievement.achievementId,
                ),
              )}
              onPositionChange={moveAchievements}
              disabled={!hasEditPermission(['employeeRecognition'])}
              items={
                achiementsFields.map((field) => {
                  const achievement = achievements?.find(
                    (a) => a.id === field.achievementId,
                  );

                  return {
                    id: field.achievementId,
                    content: achievement ? (
                      <Stack
                        sx={{
                          background:
                            achievement.status === 'inactive'
                              ? SystemColors.grey[98]
                              : SystemColors.white,
                          padding: 2,
                        }}
                        flexDirection="row"
                        alignItems="center"
                        gap={3}
                        borderRadius={1}
                        boxShadow={` 0 0 0 1px ${SystemColors.grey[90]}`}
                        key={achievement.id}
                      >
                        <SortableList.DragIndicator />

                        <AchievementImage
                          image={achievement.image?.url ?? ''}
                        />

                        <Stack flex={1} gap={0.25}>
                          <Typography variant="h3">
                            {achievement.name}
                          </Typography>
                          <Typography
                            color="textSecondary"
                            variant="h3"
                          >{`${achievement.points} ${pointsLabel}`}</Typography>
                          <Typography color="textSecondary" variant="p2">
                            {achievement.status === 'inactive'
                              ? t('status.inactive')
                              : !achievement.limitPeriod || !achievement.limit
                              ? null
                              : `${achievement.limit} ${t(
                                  `recognition.allowanceRuleFrequency-${achievement.limitPeriod}`,
                                )}`}
                          </Typography>
                        </Stack>

                        <Stack flexDirection="row" gap={1}>
                          <Button
                            icon={
                              hasEditPermission(['employeeRecognition'])
                                ? Edit
                                : undefined
                            }
                            label={
                              hasEditPermission(['employeeRecognition'])
                                ? t('common.edit')
                                : t('common.open')
                            }
                            variant="neutral-outline"
                            size="sm"
                            disabled={isRemoving}
                            onClick={() =>
                              history.push(
                                ROUTES.recognitions.achievements.details.replace(
                                  ':id',
                                  achievement.id,
                                ),
                              )
                            }
                          />
                          <Button
                            icon={Delete}
                            label={t('actions.delete')}
                            variant="neutral-outline"
                            size="sm"
                            disabled={
                              isRemoving ||
                              !hasEditPermission(['employeeRecognition'])
                            }
                            onClick={async () => {
                              const { confirmed } = await confirmation(
                                t('common.areYouSureToDelete', {
                                  title: achievement.name,
                                }),
                              );

                              if (confirmed) {
                                removeAchievement(
                                  { achievementId: achievement.id },
                                  {
                                    onSuccess: () => {
                                      predefinedToasts.success.deleted();
                                    },
                                  },
                                );
                              }
                            }}
                          />
                        </Stack>
                      </Stack>
                    ) : null,
                  };
                }) ?? []
              }
            />
          </Stack>
        </TableToolbar>
      </TableWrapper>

      <Footer visible={isDirty}>
        <Stack direction="row" justifyContent="end" gap={1}>
          <Button
            label={t('common.cancel')}
            variant="neutral-outline"
            onClick={() => {
              reset(undefined, { keepDirty: false });
            }}
          />
          <Button
            label={t('common.save')}
            variant="primary"
            type="submit"
            disabled={
              isUpdatingPriorities ||
              !hasEditPermission(['employeeRecognition'])
            }
          />
        </Stack>
      </Footer>
    </form>
  );
};
